// Reach Voice (2 October 2026): phone calls answered by the assistant.
//
// `services/voice`. Hand-typed for now: the generated client arrives the next
// time the OpenAPI documents are fetched from a running stack (the `voice`
// service is in `openapi-ts.config.ts`). The request path is the house core,
// so the envelope, token refresh and ApiError mapping are the same as everywhere.
//
// Voice is built switched off. `getStatus()` always answers and is what a
// console asks before it shows anything: `available: false` with code
// `VOICE_DISABLED` means show nothing. Every other tenant route answers 503
// `VOICE_DISABLED` while the platform has voice off.

import { http } from './_core/request.js';
import { isApiError } from './_core/errors.js';
import type { Envelope } from './_core/envelope.js';

/** Where an organisation stands with voice. */
export type VoiceTenantStatus = 'off' | 'applied' | 'active' | 'suspended';

/** The switch for each part of voice. All of them sit under the platform's master switch. */
export interface VoiceSwitches {
  inbound: boolean;
  outbound: boolean;
  ai: boolean;
  recording: boolean;
  international: boolean;
  applications: boolean;
  /** Customers may call a business's WhatsApp number, answered like a phone call (3 October 2026). */
  whatsapp?: boolean;
}

export interface VoiceStatus {
  /** False while the platform has voice switched off: show nothing of it. */
  available: boolean;
  /** `VOICE_DISABLED` or `VOICE_UNAVAILABLE` when not available. */
  code?: string | null;
  message?: string | null;
  tenant_status: VoiceTenantStatus;
  suspended_reason?: string | null;
  outbound_blocked: boolean;
  outbound_blocked_reason?: string | null;
  switches: VoiceSwitches;
  /** The terms to accept before starting; null when none are published. */
  terms_version?: string | null;
  /** Where the terms can be read. */
  terms_url?: string | null;
  terms_accepted: boolean;
  self_activation: boolean;
  /** The host a phone provider sends calls to (`sip:{number}@{sip_host}`); null until it is set. */
  sip_host?: string | null;
}

/** What the platform allows an organisation. Money is in cents. */
export interface VoiceTenantLimits {
  max_call_minutes: number;
  concurrent_calls: number;
  hourly_cap_minor: number;
  daily_cap_minor: number;
  max_extensions: number;
  max_numbers: number;
  international: boolean;
}

/** What an organisation chose for itself, inside its limits. */
export interface VoiceTenantSettings {
  limits: { max_call_minutes: number; concurrent_calls: number; hourly_cap_minor: number; daily_cap_minor: number };
  recording: { enabled: boolean; notice: string; retention_days: number };
}

export interface VoiceTenant {
  organisation_id: string;
  status: VoiceTenantStatus;
  settings: VoiceTenantSettings;
  limits: VoiceTenantLimits;
  terms_version?: string | null;
  terms_accepted_at?: string | null;
  applied_at?: string | null;
  activated_at?: string | null;
  suspended_at?: string | null;
  suspended_reason?: string | null;
  outbound_blocked_at?: string | null;
  outbound_blocked_reason?: string | null;
  updated_at: string;
  /**
   * The recording notice as speech, played before a call a person answers or places is recorded:
   * `ready`, `unavailable` (it could not be made: such calls are not recorded) or `off`.
   */
  notice_audio?: 'ready' | 'unavailable' | 'off';
}

/** A change to an organisation's own settings: send only what changes. */
export type VoiceTenantSettingsPatch = {
  limits?: Partial<VoiceTenantSettings['limits']>;
  recording?: Partial<VoiceTenantSettings['recording']>;
};

/** Every platform voice setting, with the value in force. Money is in cents. */
export interface PlatformVoiceSettings {
  switches: VoiceSwitches & { self_activation: boolean };
  limits: {
    max_call_minutes: number;
    concurrent_calls_per_tenant: number;
    concurrent_calls_platform: number;
    tenant_hourly_cap_minor: number;
    tenant_daily_cap_minor: number;
    platform_hourly_cap_minor: number;
    platform_daily_cap_minor: number;
    min_balance_minutes: number;
    billing_increment_seconds: number;
    max_extensions_per_tenant: number;
    max_numbers_per_tenant: number;
  };
  ai: { provider: string; voice: string; model: string };
  recording: { max_retention_days: number; default_retention_days: number };
  destinations: { allowed_countries: string[]; blocked_prefixes: string[] };
  /** What stops an organisation's outgoing calls by itself: a burst, many different numbers, a run of very short calls. */
  fraud: { max_outbound_per_10_min: number; max_destinations_per_hour: number; max_short_calls_per_10_min: number; short_call_seconds: number };
  /** How numbers the platform supplies are looked after: days unpaid before a number stops being answered, before it is taken back, and how long an application's fee stays held. */
  numbers: {
    rental_grace_days: number; release_after_days: number; application_hold_days: number;
    /** The documents an application for a number must carry, one upload each (RICA). Missing: the usual business set. */
    required_documents?: string[];
  };
  /** What a person has confirmed because the service cannot see it: the day the attack tests passed (yyyy-MM-dd, or empty), that the call engine has a real certificate, and how many calls at once the server was tested with. */
  attested: { attack_tests_on: string; phone_certificate: boolean; load_tested_calls: number };
  approval_mode: 'manual' | 'auto';
  terms_version: string;
  /** Where a person reads the terms (https). */
  terms_url: string;
  on_kill: 'end_now' | 'let_finish';
  /**
   * WhatsApp calls: the addresses or networks Meta's calls come from (Meta publishes them with its
   * webhook addresses). Only these reach the call engine as WhatsApp; with none, no WhatsApp call can arrive.
   */
  whatsapp?: { sources: string[] };
}

export interface PlatformVoice {
  /** The kill switch. */
  master_enabled: boolean;
  settings: PlatformVoiceSettings;
  updated_at: string;
  updated_by?: string | null;
}

/** A change to platform settings: send only what changes, section by section. */
export type PlatformVoiceSettingsPatch = { [K in keyof PlatformVoiceSettings]?: PlatformVoiceSettings[K] extends object ? Partial<PlatformVoiceSettings[K]> : PlatformVoiceSettings[K] };

export interface VoiceAuditEntry {
  id: string;
  organisation_id?: string | null;
  event: string;
  severity: 'info' | 'warning' | 'critical';
  summary: string;
  detail: Record<string, unknown>;
  actor?: string | null;
  created_at: string;
}

export interface VoiceNode {
  id: string;
  name: string;
  ari_url: string;
  public_host?: string | null;
  capacity: number;
  draining: boolean;
  is_active: boolean;
  connected: boolean;
  last_seen_at?: string | null;
  version?: string | null;
  live_calls: number;
}

/** How a phone provider connects: we sign in to it (`register`), or it sends calls to our address (`forward`). */
export type VoiceProviderMode = 'register' | 'forward';

export interface VoiceProvider {
  id: string;
  name: string;
  /** A preset code, or `generic`. It only labels the row. */
  provider: string;
  mode: VoiceProviderMode;
  host: string;
  port: number;
  transport: 'udp' | 'tcp' | 'tls';
  username?: string | null;
  /** Whether a password is stored. The password itself is never returned. */
  has_password: boolean;
  allowed_sources: string[];
  /** `new` until the call engine has checked it, then `ok` or `failing`. */
  status: 'new' | 'ok' | 'failing';
  last_error?: string | null;
  last_checked_at?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VoiceProviderInput {
  name: string;
  provider?: string;
  mode: VoiceProviderMode;
  host: string;
  port?: number;
  transport?: 'udp' | 'tcp' | 'tls';
  username?: string;
  /** Write-only. Left out on an update, the stored password is kept. */
  password?: string;
  /** Addresses or networks the provider's calls come from, when not the host itself. */
  allowed_sources?: string[];
  is_active?: boolean;
}

/** How a number is answered. */
export interface VoiceNumberHandling {
  greeting: string;
  language: string;
  voice: string;
  instructions: string;
  /** Who answers first: the assistant, or a department (the assistant answers when nobody there does). */
  answer_with: 'assistant' | 'department';
  department_id?: string | null;
}

export interface VoiceNumber {
  id: string;
  /** International form: +27871234567. */
  number: string;
  label?: string | null;
  /** `whatsapp`: a business's WhatsApp number taking WhatsApp calls, switched on from the Numbers page. */
  source: 'own' | 'platform' | 'whatsapp';
  provider_account_id?: string | null;
  /** The assistant that answers; null is the organisation's default. */
  bot_id?: string | null;
  handling: VoiceNumberHandling;
  /** `pending` (paused or not yet live), `active` or `suspended`. */
  status: 'pending' | 'active' | 'suspended';
  /** Whether the assistant can answer this number yet: `none` (being set up), `registered` (ready) or `failed`. */
  assistant_status: 'none' | 'registered' | 'failed';
  assistant_error?: string | null;
  created_at: string;
  updated_at: string;
  /** For a number from the platform: when the next rental falls due. */
  next_rental_at?: string | null;
  /** Since when a rental has gone unpaid; null when paid up. */
  rental_overdue_at?: string | null;
  /** Why a suspended number is suspended. */
  suspended_reason?: string | null;
}

/** What a number from the platform costs this organisation, in cents, VAT in. Both are paid only from bought credits. */
export interface VoiceApplicationQuote {
  /** False while the platform has applications closed. */
  open: boolean;
  /** False until both rates exist: nothing can be applied for. */
  priced: boolean;
  /** Held when applying, taken on approval. */
  setup_minor: number;
  /** Each month, from a month after approval. */
  monthly_minor: number;
  /** The documents an application must carry, one upload each, in the platform's words. */
  required_documents?: string[];
}

/** A supporting document: the requirement it answers and the file in the organisation's media library. */
export interface VoiceApplicationDocument {
  requirement: string;
  media_id: string;
}

export interface VoiceApplicationDetails {
  business_name: string;
  registration_number?: string;
  contact_name: string;
  contact_phone: string;
  address?: string;
  purpose: string;
}

export interface VoiceApplication {
  id: string;
  organisation_id: string;
  status: 'submitted' | 'approved' | 'rejected' | 'withdrawn';
  number_kind: 'national' | 'geographic';
  details: VoiceApplicationDetails;
  document_ids: string[];
  /** The supporting documents, each with the requirement it answers. */
  documents?: Array<{ requirement?: string | null; media_id: string; name?: string | null }>;
  fee_minor: number;
  monthly_minor: number;
  submitted_by?: string | null;
  decided_at?: string | null;
  decided_by?: string | null;
  /** Why it was rejected, in words written for the organisation. */
  reject_reason?: string | null;
  /** The number assigned, once approved. */
  number?: string | null;
  created_at: string;
  updated_at: string;
}

/** One of the platform's own carriers. */
export interface VoiceCarrier {
  id: string;
  name: string;
  mode: VoiceProviderMode;
  host: string;
  port: number;
  transport: 'udp' | 'tcp' | 'tls';
  username?: string | null;
  has_password: boolean;
  allowed_sources: string[];
  status: 'new' | 'ok' | 'failing';
  last_error?: string | null;
  last_checked_at?: string | null;
  is_active: boolean;
  /** Confirmed by the owner: the carrier account is prepaid or has a credit limit. */
  spend_limited: boolean;
  created_at: string;
  updated_at: string;
}

export interface VoiceCarrierInput {
  name: string;
  mode: VoiceProviderMode;
  host: string;
  port?: number;
  transport?: 'udp' | 'tcp' | 'tls';
  username?: string;
  /** Write-only. Left out on an update, the stored password is kept. */
  password?: string;
  allowed_sources?: string[];
  is_active?: boolean;
  spend_limited?: boolean;
}

/** A number the platform holds with a carrier. */
export interface VoiceInventoryNumber {
  id: string;
  carrier_id: string;
  number: string;
  kind: 'national' | 'geographic';
  status: 'available' | 'assigned' | 'retired';
  organisation_id?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface VoiceCallLeg {
  id: string;
  /** `carrier` (the phone line) or `ai` (the assistant). */
  kind: string;
  answered_at?: string | null;
  ended_at?: string | null;
  billed_seconds: number;
  charged_minor: number;
  quality: Record<string, unknown>;
}

export interface VoiceCallEvent {
  at: string;
  kind: 'caller' | 'assistant' | 'tool' | 'system';
  text: string;
}

export type VoiceCallEndReason =
  | 'completed' | 'caller_hung_up' | 'no_credit' | 'max_length' | 'kill_switch' | 'engine_lost' | 'failed' | 'ended_by_platform' | 'no_answer';

export interface VoiceCall {
  id: string;
  organisation_id: string;
  number_id?: string | null;
  direction: 'inbound' | 'outbound';
  from?: string | null;
  to?: string | null;
  status: 'ringing' | 'active' | 'ended';
  end_reason?: VoiceCallEndReason | null;
  started_at: string;
  answered_at?: string | null;
  ended_at?: string | null;
  /** The time charged for, in whole steps rounded up. */
  billed_seconds: number;
  /** What the call cost, in cents. Zero until billing has taken the charge. */
  charged_minor: number;
  per_minute_minor: number;
  answered_by_assistant: boolean;
  /** A message the assistant took, when it took one. */
  summary?: string | null;
  /** Filled by `getCall`, empty in lists. */
  legs: VoiceCallLeg[];
  transcript: VoiceCallEvent[];
  /** Who the call was passed to, in words ("Sales", "Thandi (101)"); null when it stayed with the assistant. */
  transferred_to?: string | null;
  /** The line that placed an outgoing call or took a transfer. */
  extension_id?: string | null;
  /** Where the call's recording stands: null (not recorded), `recording`, `waiting`, `stored` (it can be listened to), `failed` or `deleted`. */
  recording?: 'recording' | 'waiting' | 'stored' | 'failed' | 'deleted' | null;
  /** How the call sounded on the caller's side; null when it was too short to tell. */
  quality?: 'good' | 'fair' | 'poor' | null;
  /** `whatsapp` for a call a customer placed from WhatsApp (3 October 2026); `phone` otherwise. */
  channel?: 'phone' | 'whatsapp';
}

/** A signed address to listen at, good for a few minutes. */
export interface VoiceRecording {
  url: string;
  url_expires_at: string;
  seconds: number;
  bytes: number;
  /** When the recording is deleted. */
  expires_at?: string | null;
}

export interface VoiceReadinessItem {
  code: string;
  ok: boolean;
  /** True for a line that stops voice (or a part of it) being switched on where the checklist is enforced. */
  blocking: boolean;
  text: string;
}

/** The go-live checklist, worked out by the service. */
export interface VoiceReadiness {
  /** No blocking line is unmet. */
  ready: boolean;
  /** Every line is met. */
  complete: boolean;
  /** Whether a blocking line refuses the switch here (it does in production). */
  enforced: boolean;
  master_enabled: boolean;
  items: VoiceReadinessItem[];
}

/** What a phone or app is set up with. Returned once, with the password. */
export interface VoiceSignIn {
  /** Null until the platform has set its public address. */
  server?: string | null;
  port: number;
  transport: 'TLS';
  username: string;
  media_encryption: 'SRTP';
}

/** A person's line: a phone or app that signs in, or a forward to an outside number. */
export interface VoiceExtension {
  id: string;
  /** What colleagues dial: three or four digits. */
  number: string;
  name: string;
  kind: 'phone' | 'forward';
  forward_to?: string | null;
  /** May this line call outside numbers? Off unless switched on. */
  can_dial_out: boolean;
  /** May the assistant pass a caller to this line by name? */
  takes_transfers: boolean;
  is_active: boolean;
  /** For a phone: is a device signed in right now? */
  signed_in: boolean;
  last_seen_at?: string | null;
  secret_set_at?: string | null;
  /** Present only in the response that made or replaced the sign-in details. */
  password?: string | null;
  sign_in?: VoiceSignIn | null;
  created_at: string;
  updated_at: string;
}

export interface VoiceExtensionInput {
  number?: string;
  name?: string;
  /** Fixed once the line is made. */
  kind?: 'phone' | 'forward';
  /** For a forward: a South African number. */
  forward_to?: string;
  can_dial_out?: boolean;
  takes_transfers?: boolean;
  is_active?: boolean;
}

/** A department: lines rung together or one after the other. */
export interface VoiceGroup {
  id: string;
  name: string;
  /** What the assistant is told this department is for. */
  purpose?: string | null;
  strategy: 'together' | 'in_order';
  ring_seconds: number;
  is_active: boolean;
  /** The department's lines, in ringing order. */
  extension_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface VoiceGroupInput {
  name?: string;
  purpose?: string;
  strategy?: 'together' | 'in_order';
  ring_seconds?: number;
  /** Left out on an update, the lines are kept. */
  extension_ids?: string[];
  is_active?: boolean;
}

/** An address the call engine stopped listening to after repeated failed sign-ins. */
export interface VoiceBan {
  id: string;
  address: string;
  failures: number;
  banned_at: string;
  last_seen_at: string;
  lifted_at?: string | null;
  lifted_by?: string | null;
}

export interface VoiceCallList {
  data: VoiceCall[];
  meta: { count: number; total: number; limit: number };
}

/** True when the error is "voice is switched off" (or cannot be confirmed as on). */
export function isVoiceDisabled(e: unknown): boolean {
  return isApiError(e) && e.status === 503 && (e.code === 'VOICE_DISABLED' || e.code === 'VOICE_UNAVAILABLE');
}

// ── an organisation ─────────────────────────────────────────────────────────

/** Whether voice is available and where this organisation stands. Always answers. */
export function getStatus(): Promise<Envelope<VoiceStatus>> {
  return http.get<Envelope<VoiceStatus>>('/v1/voice/status');
}

/** Accept the voice terms and ask for voice. Becomes `applied`, or `active` when the platform approves automatically. */
export function start(termsVersion: string): Promise<Envelope<VoiceTenant>> {
  return http.post<Envelope<VoiceTenant>>('/v1/voice/start', { body: { terms_version: termsVersion } });
}

export function getSettings(): Promise<Envelope<VoiceTenant>> {
  return http.get<Envelope<VoiceTenant>>('/v1/voice/settings');
}

/** A limit can be lowered, never raised above what the platform allows: a higher value is stored as the allowed one. */
export function updateSettings(settings: VoiceTenantSettingsPatch): Promise<Envelope<VoiceTenant>> {
  return http.put<Envelope<VoiceTenant>>('/v1/voice/settings', { body: { settings } });
}

// ── phone providers ─────────────────────────────────────────────────────────

export function listProviders(): Promise<Envelope<VoiceProvider[]>> {
  return http.get<Envelope<VoiceProvider[]>>('/v1/voice/providers');
}

/** Connect a phone provider. The password is stored sealed and never returned. */
export function createProvider(input: VoiceProviderInput): Promise<Envelope<VoiceProvider>> {
  return http.post<Envelope<VoiceProvider>>('/v1/voice/providers', { body: input });
}

/** Change a provider. Leave `password` out to keep the one stored. */
export function updateProvider(id: string, input: VoiceProviderInput): Promise<Envelope<VoiceProvider>> {
  return http.put<Envelope<VoiceProvider>>(`/v1/voice/providers/${encodeURIComponent(id)}`, { body: input });
}

/** Refused (409 `VOICE_PROVIDER_IN_USE`) while a number still comes through it. */
export function deleteProvider(id: string): Promise<void> {
  return http.delete<void>(`/v1/voice/providers/${encodeURIComponent(id)}`);
}

// ── numbers ─────────────────────────────────────────────────────────────────

export function listNumbers(): Promise<Envelope<VoiceNumber[]>> {
  return http.get<Envelope<VoiceNumber[]>>('/v1/voice/numbers');
}

/** Add a number that comes through one of the organisation's providers. 409 `VOICE_NUMBER_TAKEN` or `VOICE_NUMBER_LIMIT`. */
export function createNumber(input: { number: string; label?: string; provider_account_id: string; bot_id?: string; handling?: Partial<VoiceNumberHandling> }): Promise<Envelope<VoiceNumber>> {
  return http.post<Envelope<VoiceNumber>>('/v1/voice/numbers', { body: input });
}

/** Change how a number is answered, or pause it. Send only what changes; `bot_id` of all zeros clears the assistant. */
export function updateNumber(id: string, input: { label?: string; bot_id?: string; handling?: Partial<VoiceNumberHandling>; paused?: boolean }): Promise<Envelope<VoiceNumber>> {
  return http.put<Envelope<VoiceNumber>>(`/v1/voice/numbers/${encodeURIComponent(id)}`, { body: input });
}

export function deleteNumber(id: string): Promise<void> {
  return http.delete<void>(`/v1/voice/numbers/${encodeURIComponent(id)}`);
}

// ── calls ───────────────────────────────────────────────────────────────────

/** The organisation's calls, newest first. Still answers for a suspended organisation. */
export function listCalls(opts: { limit?: number; offset?: number } = {}): Promise<VoiceCallList> {
  return http.get<VoiceCallList>('/v1/voice/calls', { query: { limit: opts.limit, offset: opts.offset } });
}

/** One call with its sides, what it cost and what was said. */
export function getCall(id: string): Promise<Envelope<VoiceCall>> {
  return http.get<Envelope<VoiceCall>>(`/v1/voice/calls/${encodeURIComponent(id)}`);
}

// ── applying for a number ───────────────────────────────────────────────────

/** What a number from the platform costs, and whether applications are open. Answers once the voice terms are accepted. */
export function getApplicationQuote(): Promise<Envelope<VoiceApplicationQuote>> {
  return http.get<Envelope<VoiceApplicationQuote>>('/v1/voice/applications/quote');
}

export function listApplications(): Promise<Envelope<VoiceApplication[]>> {
  return http.get<Envelope<VoiceApplication[]>>('/v1/voice/applications');
}

/**
 * Apply for a number. The registration fee is held from bought credits until the platform decides.
 * 402 `VOICE_NO_CREDIT` when they do not cover it (a plan's included credits cannot pay a fee);
 * 409 `VOICE_APPLICATION_OPEN` while another is waiting; 503 `VOICE_APPLICATIONS_OFF` when closed.
 */
/**
 * `documents`: one per `required_documents` on the quote, each a file this organisation uploaded to
 * media. 400 `VOICE_DOCUMENTS_MISSING` names what is missing; 400 `VOICE_DOCUMENT_NOT_FOUND` when a
 * file is not the organisation's.
 */
export function apply(input: { number_kind?: 'national' | 'geographic'; details: VoiceApplicationDetails; documents?: VoiceApplicationDocument[]; document_ids?: string[]; terms_version?: string }): Promise<Envelope<VoiceApplication>> {
  return http.post<Envelope<VoiceApplication>>('/v1/voice/applications', { body: input });
}

/** Withdraw an application that is still waiting. The held fee is given back. */
export function withdrawApplication(id: string): Promise<Envelope<VoiceApplication>> {
  return http.post<Envelope<VoiceApplication>>(`/v1/voice/applications/${encodeURIComponent(id)}/withdraw`, {});
}

// ── lines and departments ───────────────────────────────────────────────────

export function listExtensions(): Promise<Envelope<VoiceExtension[]>> {
  return http.get<Envelope<VoiceExtension[]>>('/v1/voice/extensions');
}

/** Add a line. For a phone the response carries `password` and `sign_in`, once. 409 `VOICE_EXTENSION_TAKEN` or `VOICE_EXTENSION_LIMIT`. */
export function createExtension(input: VoiceExtensionInput): Promise<Envelope<VoiceExtension>> {
  return http.post<Envelope<VoiceExtension>>('/v1/voice/extensions', { body: input });
}

/** Change a line. Send only what changes. Allowing a line to call out is recorded on the audit trail. */
export function updateExtension(id: string, input: VoiceExtensionInput): Promise<Envelope<VoiceExtension>> {
  return http.put<Envelope<VoiceExtension>>(`/v1/voice/extensions/${encodeURIComponent(id)}`, { body: input });
}

/** Replace a phone's sign-in details. The device using the old ones is shut out; the new `password` is returned once. */
export function replaceExtensionSecret(id: string): Promise<Envelope<VoiceExtension>> {
  return http.post<Envelope<VoiceExtension>>(`/v1/voice/extensions/${encodeURIComponent(id)}/secret`, {});
}

export function deleteExtension(id: string): Promise<void> {
  return http.delete<void>(`/v1/voice/extensions/${encodeURIComponent(id)}`);
}

export function listGroups(): Promise<Envelope<VoiceGroup[]>> {
  return http.get<Envelope<VoiceGroup[]>>('/v1/voice/groups');
}

export function createGroup(input: VoiceGroupInput): Promise<Envelope<VoiceGroup>> {
  return http.post<Envelope<VoiceGroup>>('/v1/voice/groups', { body: input });
}

export function updateGroup(id: string, input: VoiceGroupInput): Promise<Envelope<VoiceGroup>> {
  return http.put<Envelope<VoiceGroup>>(`/v1/voice/groups/${encodeURIComponent(id)}`, { body: input });
}

export function deleteGroup(id: string): Promise<void> {
  return http.delete<void>(`/v1/voice/groups/${encodeURIComponent(id)}`);
}

/**
 * Pass a call in progress to a line or a department (exactly one of the two). Rings them while the
 * caller stays where they are; resolves when somebody answers, and fails with 409
 * `VOICE_TRANSFER_NO_ANSWER` when nobody does (the call is then as it was).
 */
export function transferCall(id: string, target: { extensionId?: string; groupId?: string }): Promise<Envelope<{ transferred: boolean; to: string }>> {
  return http.post<Envelope<{ transferred: boolean; to: string }>>(`/v1/voice/calls/${encodeURIComponent(id)}/transfer`, {
    body: { extension_id: target.extensionId, group_id: target.groupId }
  });
}

/**
 * A signed address to listen to a call's recording, good for five minutes. 404 `VOICE_RECORDING_NOT_FOUND` when the
 * call was not recorded; 409 `VOICE_RECORDING_NOT_READY` while it is still being uploaded, or once it has been deleted.
 * Each request is written to the audit trail.
 */
export function getCallRecording(id: string): Promise<Envelope<VoiceRecording>> {
  return http.get<Envelope<VoiceRecording>>(`/v1/voice/calls/${encodeURIComponent(id)}/recording`);
}

// ── the platform ────────────────────────────────────────────────────────────

export function adminGetSettings(): Promise<Envelope<PlatformVoice>> {
  return http.get<Envelope<PlatformVoice>>('/v1/admin/voice/settings');
}

/**
 * Change platform settings or the master switch. Changing `masterEnabled` needs a `reason`.
 * Switching on is refused (409 `VOICE_NOT_READY`) until the voice terms are published.
 */
export function adminUpdateSettings(req: { masterEnabled?: boolean; settings?: PlatformVoiceSettingsPatch; reason?: string }): Promise<Envelope<PlatformVoice>> {
  return http.put<Envelope<PlatformVoice>>('/v1/admin/voice/settings', {
    body: { master_enabled: req.masterEnabled, settings: req.settings, reason: req.reason }
  });
}

/** The kill switch: switches voice off now and ends every call in progress. */
export function adminKill(reason: string): Promise<Envelope<{ master_enabled: boolean; calls_ended: number }>> {
  return http.post<Envelope<{ master_enabled: boolean; calls_ended: number }>>('/v1/admin/voice/kill', { body: { reason } });
}

export function adminListTenants(status?: VoiceTenantStatus): Promise<Envelope<VoiceTenant[]>> {
  return http.get<Envelope<VoiceTenant[]>>('/v1/admin/voice/tenants', { query: { status } });
}

/** Switch an organisation's voice on or off, suspend it (a `reason` is required), set its limits, or lift an outbound block. */
export function adminUpdateTenant(organisationId: string, req: { status?: VoiceTenantStatus; limits?: Partial<VoiceTenantLimits>; reason?: string; outboundBlocked?: boolean }): Promise<Envelope<VoiceTenant>> {
  return http.put<Envelope<VoiceTenant>>(`/v1/admin/voice/tenants/${encodeURIComponent(organisationId)}`, {
    body: { status: req.status, limits: req.limits, reason: req.reason, outbound_blocked: req.outboundBlocked }
  });
}

export function adminListAudit(opts: { organisationId?: string; severity?: VoiceAuditEntry['severity']; limit?: number } = {}): Promise<Envelope<VoiceAuditEntry[]>> {
  return http.get<Envelope<VoiceAuditEntry[]>>('/v1/admin/voice/audit', { query: { organisationId: opts.organisationId, severity: opts.severity, limit: opts.limit } });
}

export function adminListNodes(): Promise<Envelope<VoiceNode[]>> {
  return http.get<Envelope<VoiceNode[]>>('/v1/admin/voice/nodes');
}

/** Calls across every organisation, newest first; `live` for those in progress. */
export function adminListCalls(opts: { live?: boolean; organisationId?: string; limit?: number } = {}): Promise<VoiceCallList> {
  return http.get<VoiceCallList>('/v1/admin/voice/calls', { query: { live: opts.live, organisationId: opts.organisationId, limit: opts.limit } });
}

/** End a call in progress. It is charged for the time it ran. 404 `VOICE_CALL_NOT_LIVE` when it is already over. */
export function adminEndCall(id: string): Promise<Envelope<{ ended: boolean }>> {
  return http.post<Envelope<{ ended: boolean }>>(`/v1/admin/voice/calls/${encodeURIComponent(id)}/end`, {});
}

/** Addresses the call engine stopped listening to after repeated failed sign-ins, newest first. */
export function adminListBans(): Promise<Envelope<VoiceBan[]>> {
  return http.get<Envelope<VoiceBan[]>>('/v1/admin/voice/bans');
}

/** Lift a ban. The call engine drops it within seconds. */
export function adminLiftBan(id: string): Promise<Envelope<VoiceBan>> {
  return http.post<Envelope<VoiceBan>>(`/v1/admin/voice/bans/${encodeURIComponent(id)}/lift`, {});
}

/** Applications for a number, those waiting first. */
export function adminListApplications(status?: VoiceApplication['status']): Promise<Envelope<VoiceApplication[]>> {
  return http.get<Envelope<VoiceApplication[]>>('/v1/admin/voice/applications', { query: { status } });
}

/** Approve and assign a number from the inventory. The held fee is taken, once: approving twice does not charge twice. */
export function adminApproveApplication(id: string, inventoryId: string): Promise<Envelope<VoiceApplication>> {
  return http.post<Envelope<VoiceApplication>>(`/v1/admin/voice/applications/${encodeURIComponent(id)}/approve`, { body: { inventory_id: inventoryId } });
}

/** A five-minute link to a document on an application. Each opening is written to the voice audit. */
export function adminOpenApplicationDocument(id: string, mediaId: string): Promise<Envelope<{ url: string; expires_at: string; name?: string | null; mime?: string | null }>> {
  return http.get<Envelope<{ url: string; expires_at: string; name?: string | null; mime?: string | null }>>(
    `/v1/admin/voice/applications/${encodeURIComponent(id)}/documents/${encodeURIComponent(mediaId)}`);
}

/** Reject, with a reason the organisation is shown. The held fee is given back. */
export function adminRejectApplication(id: string, reason: string): Promise<Envelope<VoiceApplication>> {
  return http.post<Envelope<VoiceApplication>>(`/v1/admin/voice/applications/${encodeURIComponent(id)}/reject`, { body: { reason } });
}

export function adminListCarriers(): Promise<Envelope<VoiceCarrier[]>> {
  return http.get<Envelope<VoiceCarrier[]>>('/v1/admin/voice/carriers');
}

export function adminCreateCarrier(input: VoiceCarrierInput): Promise<Envelope<VoiceCarrier>> {
  return http.post<Envelope<VoiceCarrier>>('/v1/admin/voice/carriers', { body: input });
}

export function adminUpdateCarrier(id: string, input: VoiceCarrierInput): Promise<Envelope<VoiceCarrier>> {
  return http.put<Envelope<VoiceCarrier>>(`/v1/admin/voice/carriers/${encodeURIComponent(id)}`, { body: input });
}

/** Refused (409 `VOICE_CARRIER_IN_USE`) while numbers are held with it. */
export function adminDeleteCarrier(id: string): Promise<void> {
  return http.delete<void>(`/v1/admin/voice/carriers/${encodeURIComponent(id)}`);
}

export function adminListInventory(status?: VoiceInventoryNumber['status']): Promise<Envelope<VoiceInventoryNumber[]>> {
  return http.get<Envelope<VoiceInventoryNumber[]>>('/v1/admin/voice/inventory', { query: { status } });
}

/** Add numbers held with a carrier. Numbers already held, or in use by an organisation, are skipped and named. */
export function adminAddInventory(input: { carrier_id: string; numbers: string[]; kind?: 'national' | 'geographic'; notes?: string }): Promise<Envelope<{ added: VoiceInventoryNumber[]; skipped: string[] }>> {
  return http.post<Envelope<{ added: VoiceInventoryNumber[]; skipped: string[] }>>('/v1/admin/voice/inventory', { body: input });
}

/** Retire a number that is not assigned, or bring it back. */
export function adminUpdateInventory(id: string, input: { retired?: boolean; notes?: string }): Promise<Envelope<VoiceInventoryNumber>> {
  return http.put<Envelope<VoiceInventoryNumber>>(`/v1/admin/voice/inventory/${encodeURIComponent(id)}`, { body: input });
}

/** The go-live checklist: what is in place, what is missing, and which lines stop voice being switched on. */
export function adminGetReadiness(): Promise<Envelope<VoiceReadiness>> {
  return http.get<Envelope<VoiceReadiness>>('/v1/admin/voice/readiness');
}
